/* eslint-disable no-nested-ternary */
import { ArrowPathIcon, ChevronLeftIcon } from '@heroicons/react/24/outline';
import { useDispatch } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { AudioConversationPreview } from '@/components/audio-conversation-preview';
import { ConversationViewer } from '@/components/conversation-viewer';
import {
  artifactDigestApiClient,
  useRegenerateArtifactDigestMutation,
} from '@/services/api/artifactDigestService';
import {
  artifactApi,
  useGetArtifactUnpackedQuery,
} from '@/services/api/artifactService';
import { ArtifactType } from '@/services/types/artifact-digest-types';
import { ArtifactUnpacked } from '@/services/types/artifact-types';

import { DigestDebugTab } from '../artifact-inspector/DigestDebugTab';
import { BlobPreview } from '../documents-v2/components/inspector/BlobPreview';
import { ArtifactTypeBadge } from './components/ArtifactTypeBadge';
import {
  formatBytes,
  formatDate,
  formatDuration,
  getFileExtension,
} from './utils/artifactFormatters';

interface ArtifactDetailPageProps {
  matterId: string;
}

const getArtifactDisplayData = (artifact: ArtifactUnpacked) => {
  const { artifact_type, created_at, artifact_digest } = artifact;
  const formattedDate = formatDate(created_at);

  switch (artifact_type) {
    case ArtifactType.BLOB: {
      const blob = artifact.blob!;
      const fileSize = formatBytes(blob.file_size);
      const extension = getFileExtension(blob.name);
      const subtitle =
        artifact_digest?.one_line_summary ||
        (extension
          ? `${extension}${fileSize ? ` • ${fileSize}` : ''}`
          : fileSize);

      return {
        title:
          artifact_digest?.title ||
          blob.name ||
          blob.original_filename ||
          'File',
        subtitle,
        date: formattedDate,
      };
    }

    case ArtifactType.TRANSCRIPT: {
      const transcript = artifact.transcript!;
      const call = transcript.conversation;
      const callDetails = call
        ? `${call.is_inbound ? 'Inbound' : 'Outbound'}${formatDuration(call.duration) ? ` • ${formatDuration(call.duration)}` : ''}`
        : undefined;

      return {
        title:
          artifact_digest?.title ||
          call?.name ||
          `${call?.is_inbound ? 'Inbound' : 'Outbound'} Call` ||
          'Transcript',
        subtitle: artifact_digest?.one_line_summary || callDetails,
        date: formattedDate,
      };
    }

    case ArtifactType.CONVERSATION: {
      const conversation = artifact.conversation!;
      const messages = conversation.messages || [];
      const firstMessage = messages[0];
      const messageCount = `${messages.length} message${messages.length !== 1 ? 's' : ''}`;

      return {
        title:
          artifact_digest?.title ||
          firstMessage?.subject ||
          firstMessage?.blurb ||
          (conversation.is_client_sms_conversation
            ? 'SMS Thread'
            : 'Email Thread'),
        subtitle: artifact_digest?.one_line_summary || messageCount,
        date: formattedDate,
      };
    }

    case ArtifactType.STAFF_NOTE: {
      const note = artifact.staff_note!;
      return {
        title: artifact_digest?.title || 'Staff Note',
        subtitle:
          artifact_digest?.one_line_summary ||
          (note.content.length > 60
            ? `${note.content.slice(0, 60)}...`
            : note.content),
        date: formattedDate,
      };
    }

    default: {
      return {
        title: artifact_digest?.title || 'Unknown Artifact',
        subtitle: artifact_digest?.one_line_summary,
        date: formattedDate,
      };
    }
  }
};

export const ArtifactDetailPage = ({ matterId }: ArtifactDetailPageProps) => {
  const { artifact_id: artifactId } = useParams<{ artifact_id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [regenerateDigest, { isLoading: isRegenerating }] =
    useRegenerateArtifactDigestMutation();

  // Fetch the unpacked artifact data (includes digest & type)
  const { data: unpackedArtifact, isLoading: isLoadingArtifact } =
    useGetArtifactUnpackedQuery(
      {
        matterId,
        artifactId: artifactId ?? '',
      },
      {
        skip: !artifactId || !matterId,
      },
    );

  // Handlers ------------------------------------------------------
  const handleRefreshDigest = async () => {
    if (!artifactId) return;

    try {
      // Invalidate the artifact digest cache
      dispatch(
        artifactDigestApiClient.util.invalidateTags([
          { type: 'ArtifactDigests', id: artifactId },
        ]),
      );

      // Invalidate the artifact cache
      dispatch(
        artifactApi.util.invalidateTags([{ type: 'Artifact', id: artifactId }]),
      );

      toast.success('Artifact digest refreshed');
    } catch (error) {
      toast.error('Failed to refresh artifact digest');
    }
  };

  const handleRegenerateDigest = async () => {
    if (!artifactId) return;

    try {
      await regenerateDigest(artifactId).unwrap();
      toast.success('Artifact digest regeneration initiated');
    } catch (error) {
      toast.error('Failed to regenerate artifact digest');
    }
  };

  // Render helpers ------------------------------------------------
  const renderPreviewContent = () => {
    if (isLoadingArtifact) {
      return (
        <div className="flex items-center justify-center h-32">
          <ArrowPathIcon className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      );
    }

    if (!unpackedArtifact) {
      return (
        <div className="flex items-center justify-center h-32 text-muted-foreground text-sm">
          No preview available
        </div>
      );
    }

    if (
      unpackedArtifact.artifact_type === ArtifactType.BLOB &&
      unpackedArtifact.blob
    ) {
      return <BlobPreview file={unpackedArtifact.blob} />;
    }

    if (unpackedArtifact.artifact_type === ArtifactType.AUDIO_CONVERSATION) {
      return <AudioConversationPreview artifact={unpackedArtifact} />;
    }

    if (unpackedArtifact.artifact_type === ArtifactType.TRANSCRIPT) {
      return <AudioConversationPreview artifact={unpackedArtifact} />;
    }

    if (unpackedArtifact.artifact_type === ArtifactType.CONVERSATION) {
      return <ConversationViewer artifact={unpackedArtifact} />;
    }

    return (
      <div className="flex items-center justify-center h-32 text-muted-foreground text-sm">
        No preview available for this artifact type
      </div>
    );
  };

  const renderDebugContent = () => {
    if (isLoadingArtifact) {
      return (
        <div className="flex items-center justify-center h-32">
          <ArrowPathIcon className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      );
    }

    if (!unpackedArtifact) {
      return (
        <div className="flex items-center justify-center h-32 text-muted-foreground text-sm">
          No artifact information available
        </div>
      );
    }

    return <DigestDebugTab unpackedArtifact={unpackedArtifact} />;
  };

  // If no artifact id redirect back
  if (!artifactId) {
    navigate('..');
    return null;
  }

  const displayData = unpackedArtifact
    ? getArtifactDisplayData(unpackedArtifact)
    : null;
  const isProcessing = unpackedArtifact && !unpackedArtifact.artifact_digest;

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="border-b bg-background px-6 py-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between">
            {/* Left: Navigation and Artifact Info */}
            <div className="flex items-center gap-4 min-w-0 flex-1">
              <Link
                to=".."
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
                aria-label="Back to artifacts list"
              >
                <ChevronLeftIcon className="h-4 w-4" />
                Back
              </Link>

              {displayData && unpackedArtifact ? (
                <div className="flex items-center gap-3 min-w-0">
                  <ArtifactTypeBadge type={unpackedArtifact.artifact_type} />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h1 className="text-sm font-medium text-foreground truncate">
                        {displayData.title}
                      </h1>
                      {isProcessing && (
                        <span
                          className="h-1.5 w-1.5 rounded-full bg-amber-400 flex-shrink-0"
                          title="Processing"
                        />
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground/70 truncate">
                      {displayData.subtitle} • {displayData.date}
                    </p>
                  </div>
                </div>
              ) : isLoadingArtifact ? (
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-muted rounded animate-pulse" />
                  <div>
                    <div className="h-3 w-32 bg-muted rounded animate-pulse mb-1" />
                    <div className="h-2 w-24 bg-muted/60 rounded animate-pulse" />
                  </div>
                </div>
              ) : null}
            </div>

            {/* Right: Actions */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={handleRefreshDigest}
                disabled={isLoadingArtifact}
                className="px-2 py-1 text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                aria-label="Refresh"
                title="Refresh Digest"
              >
                <ArrowPathIcon
                  className={`h-4 w-4 ${isLoadingArtifact ? 'animate-spin' : ''}`}
                />
              </button>
              <button
                type="button"
                onClick={handleRegenerateDigest}
                disabled={isRegenerating}
                className="px-3 py-1.5 text-xs bg-primary/10 text-primary hover:bg-primary/15 rounded font-medium transition-colors disabled:opacity-50"
                aria-label="Regenerate"
              >
                {isRegenerating ? (
                  <ArrowPathIcon className="h-3 w-3 animate-spin" />
                ) : (
                  'Regenerate'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row gap-6 h-full px-6 py-4">
          {/* Preview panel */}
          <div className="md:w-3/5 w-full border rounded-lg bg-background shadow-sm overflow-auto">
            {renderPreviewContent()}
          </div>

          {/* Debug panel */}
          <div className="md:w-2/5 w-full border rounded-lg bg-background shadow-sm overflow-auto md:max-h-none max-h-72">
            {renderDebugContent()}
          </div>
        </div>
      </div>
    </div>
  );
};
